import { memo, useCallback, useEffect, useMemo, useReducer } from 'react'
import { createPortal } from 'react-dom'
import PropTypes from 'prop-types'
import { useCloudContext } from '../contexts/CloudProvider.jsx'
import { useDevicePairing } from '../hooks/useDevicePairing.js'
import LoadingSpinner from './LoadingSpinner.jsx'

// Initial state and action types
const INITIAL_STATE = {
  step: 1,
  formData: {
    serialNumber: '',
    nickname: '',
    location: '',
    pairingMethod: 'serial',
  },
  loading: false,
  error: '',
  accessRequestSent: false,
  accessRequestMessage: '',
}

const ACTIONS = {
  SET_STEP: 'SET_STEP',
  UPDATE_FORM: 'UPDATE_FORM',
  SET_LOADING: 'SET_LOADING',
  SET_ERROR: 'SET_ERROR',
  SET_ACCESS_REQUEST: 'SET_ACCESS_REQUEST',
  RESET: 'RESET',
}

// State reducer for better state management
const wizardReducer = (state, action) => {
  switch (action.type) {
    case ACTIONS.SET_STEP:
      return { ...state, step: action.payload, error: '', accessRequestSent: false }
    case ACTIONS.UPDATE_FORM:
      return { 
        ...state, 
        formData: { ...state.formData, [action.field]: action.value },
        error: '',
        accessRequestSent: false,
      }
    case ACTIONS.SET_LOADING:
      return { ...state, loading: action.payload }
    case ACTIONS.SET_ERROR:
      return { ...state, error: action.payload, loading: false, accessRequestSent: false }
    case ACTIONS.SET_ACCESS_REQUEST:
      return { 
        ...state, 
        accessRequestSent: true, 
        accessRequestMessage: action.payload, 
        loading: false, 
        error: '',
      }
    case ACTIONS.RESET:
      return INITIAL_STATE
    default:
      return state
  }
}

// Step configuration for better maintainability
const STEPS = [
  { id: 1, label: 'Device ID', icon: 'bi-hash' },
  { id: 2, label: 'Details', icon: 'bi-gear' },
  { id: 3, label: 'Confirm', icon: 'bi-check-circle' },
]

/**
 * Device Pairing Wizard Component
 * Walks users through the process of adding a new device to their account
 */
const DevicePairingWizard = memo(({ isOpen, onClose, initialSerialNumber = null }) => {
  const [state, dispatch] = useReducer(wizardReducer, INITIAL_STATE)
  const { user } = useCloudContext()
  const { pairDevice, validateDevice } = useDevicePairing()

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      dispatch({ type: ACTIONS.RESET })
      
      // Pre-populate serial number if provided
      if (initialSerialNumber) {
        dispatch({ 
          type: ACTIONS.UPDATE_FORM, 
          field: 'serialNumber', 
          value: initialSerialNumber,
        })
      }
    }
  }, [isOpen, initialSerialNumber])

  // Memoized handlers to prevent unnecessary re-renders
  const handleInputChange = useCallback((field, value) => {
    dispatch({ type: ACTIONS.UPDATE_FORM, field, value })
  }, [])
  
  const handleNext = useCallback(async () => {
    if (state.step === 1 && !state.formData.serialNumber.trim()) {
      dispatch({ type: ACTIONS.SET_ERROR, payload: 'Please enter a serial number' })
      return
    }
    
    // If moving from step 1 to step 2, validate the serial number and check for conflicts
    if (state.step === 1) {
      dispatch({ type: ACTIONS.SET_LOADING, payload: true })
      
      try {
        const validation = await validateDevice(state.formData.serialNumber)
        
        if (!validation.isValid) {
          dispatch({ 
            type: ACTIONS.SET_ERROR, 
            payload: validation.message || 'Invalid serial number',
          })
          return
        }
        
        if (!validation.isAvailable) {
          if (validation.conflictType === 'alreadyOwned') {
            // Device already in their account - close wizard
            onClose()
            dispatch({ type: ACTIONS.RESET })
            return
          } else if (validation.conflictType === 'pendingRequest') {
            // User already has a pending request - show message
            dispatch({ 
              type: ACTIONS.SET_ACCESS_REQUEST, 
              payload: validation.message || 'You already have a pending access request for this device.',
            })
            return
          } else if (validation.conflictType === 'ownedByOther') {
            // Device owned by someone else - create access request
            const result = await pairDevice({
              serialNumber: state.formData.serialNumber,
              nickname: null,
              location: null,
            })
            
            if (result.accessRequestCreated) {
              dispatch({ 
                type: ACTIONS.SET_ACCESS_REQUEST, 
                payload: result.message || 'Access request has been sent to device administrators.',
              })
              return
            } else if (result.accessRequestCreated === false) {
              // Existing request found
              dispatch({ 
                type: ACTIONS.SET_ACCESS_REQUEST, 
                payload: result.message || 'You already have a pending access request for this device.',
              })
              return
            }
          }
        }
        
        // Device is valid and available - continue to step 2
        dispatch({ type: ACTIONS.SET_LOADING, payload: false })
        if (state.step < 3) {
          dispatch({ type: ACTIONS.SET_STEP, payload: state.step + 1 })
        }
      } catch (err) {
        dispatch({ 
          type: ACTIONS.SET_ERROR, 
          payload: err.message || 'Failed to validate device. Please try again.',
        })
      }
    } else {
      // For other steps, just continue normally
      if (state.step < 3) {
        dispatch({ type: ACTIONS.SET_STEP, payload: state.step + 1 })
      }
    }
  }, [state.step, state.formData.serialNumber, validateDevice, pairDevice, onClose])
  
  const handleBack = useCallback(() => {
    if (state.step > 1) {
      dispatch({ type: ACTIONS.SET_STEP, payload: state.step - 1 })
    }
  }, [state.step])
  
  const handleSubmit = useCallback(async () => {
    dispatch({ type: ACTIONS.SET_LOADING, payload: true })
    
    try {
      const result = await pairDevice({
        serialNumber: state.formData.serialNumber,
        nickname: state.formData.nickname || null,
        location: state.formData.location || null,
      })
      
      // Success! Close wizard and reset form
      onClose()
      dispatch({ type: ACTIONS.RESET })
      
      // Show success message if device was already paired
      if (result.alreadyPaired) {
        // Device was already in account - could show toast notification
      }
    } catch (err) {
      dispatch({ 
        type: ACTIONS.SET_ERROR, 
        payload: err.message || 'Failed to pair device. Please try again.',
      })
    }
  }, [pairDevice, state.formData, onClose])

  // Memoized progress calculation
  const progressWidth = useMemo(() => 
    `${((state.step - 1) / 2) * 100}%`, [state.step],
  )

  // Memoized step components for better performance
  const stepComponents = useMemo(() => ({
    1: <DeviceIdStep formData={state.formData} onInputChange={handleInputChange} />,
    2: <DeviceDetailsStep formData={state.formData} onInputChange={handleInputChange} />,
    3: <ConfirmationStep formData={state.formData} user={user} />,
  }), [state.formData, handleInputChange, user])
  
  // Early return for modal visibility
  if (!isOpen) return null
  
  return createPortal(
    <div className="modal show d-block" style={{ backgroundColor: 'var(--modal-backdrop)', zIndex: 'var(--z-modal-high)' }}>
      <div className="modal-dialog modal-lg modal-dialog-centered">
        <div className="modal-content border-0 shadow-lg">
          <div className="modal-header border-0 pb-1">
            <div>
              <h5 className="modal-title fw-bold mb-0">
                <i className="bi bi-plus-circle text-primary me-2"></i>
                Add New Device
              </h5>
              <p className="text-muted small mb-0">Connect your Revatek device to start monitoring</p>
            </div>
            <button 
              type="button" 
              className="btn-close" 
              onClick={onClose}
              disabled={state.loading}
            />
          </div>
          
          <div className="modal-body pt-1 pb-2">
            {!state.accessRequestSent && (
              <ProgressSteps steps={STEPS} currentStep={state.step} progressWidth={progressWidth} />
            )}
            
            {state.error && (
              <div className="alert alert-danger border-0 bg-danger bg-opacity-10 py-2 mb-3" role="alert">
                <i className="bi bi-exclamation-triangle-fill text-danger me-2"></i>
                <strong>Error:</strong> {state.error}
              </div>
            )}
            
            {state.accessRequestSent && (
              <div className="alert alert-info border-0 bg-info bg-opacity-10 py-3 mb-3" role="alert">
                <div className="d-flex align-items-start">
                  <i className="bi bi-info-circle-fill text-info me-3 mt-1"></i>
                  <div className="flex-grow-1">
                    <h6 className="alert-heading fw-semibold mb-2">Access Request Sent</h6>
                    <p className="mb-2">{state.accessRequestMessage}</p>
                    <p className="mb-3 small text-muted">
                      <i className="bi bi-clock me-1"></i>
                      You&apos;ll receive a notification when your request is approved.
                    </p>
                    <button 
                      type="button" 
                      className="btn btn-outline-primary btn-sm"
                      onClick={() => dispatch({ type: ACTIONS.RESET })}
                    >
                      <i className="bi bi-plus-circle me-1"></i>
                      Try Another Device
                    </button>
                  </div>
                </div>
              </div>
            )}
            
            {!state.accessRequestSent && stepComponents[state.step]}
          </div>
          
          <div className="modal-footer border-0 pt-1">
            <div className="d-flex justify-content-between align-items-center w-100">
              <div>
                {state.step > 1 && !state.accessRequestSent && (
                  <button 
                    type="button" 
                    className="btn btn-outline-secondary px-3 py-2"
                    onClick={handleBack}
                    disabled={state.loading}
                  >
                    <i className="bi bi-arrow-left me-2"></i>
                    Back
                  </button>
                )}
              </div>
              
              <div className="d-flex gap-2">
                {state.accessRequestSent ? (
                  // Access request sent - show close button
                  <button 
                    type="button" 
                    className="btn btn-primary px-4 py-2 fw-semibold" 
                    onClick={onClose}
                  >
                    <i className="bi bi-check-circle me-2"></i>
                    Close
                  </button>
                ) : (
                  // Normal flow - show cancel and continue/submit buttons
                  <>
                    <button 
                      type="button" 
                      className="btn btn-outline-secondary px-3 py-2" 
                      onClick={onClose}
                      disabled={state.loading}
                    >
                      Cancel
                    </button>
                    
                    {state.step < 3 ? (
                      <button 
                        type="button" 
                        className="btn btn-primary px-3 py-2"
                        onClick={handleNext}
                        disabled={state.loading}
                      >
                        Continue
                        <i className="bi bi-arrow-right ms-2"></i>
                      </button>
                    ) : (
                      <button 
                        type="button" 
                        className="btn btn-success px-3 py-2 fw-semibold"
                        onClick={handleSubmit}
                        disabled={state.loading}
                      >
                        {state.loading ? (
                          <>
                            <LoadingSpinner size="sm" className="me-2" />
                            Adding Device...
                          </>
                        ) : (
                          <>
                            <i className="bi bi-plus-circle-fill me-2"></i>
                            Add Device
                          </>
                        )}
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  )
})

DevicePairingWizard.displayName = 'DevicePairingWizard'

// Memoized sub-components for better performance
const ProgressSteps = memo(({ steps, currentStep, progressWidth }) => (
  <div className="mb-3">
    <div className="d-flex justify-content-between align-items-center position-relative">
      {/* Progress Line */}
      <div className="position-absolute top-50 start-0 translate-middle-y w-100" style={{ height: '2px', backgroundColor: 'var(--bg-tertiary)', zIndex: 'var(--z-base)' }}>
        <div 
          className="h-100 bg-primary transition-all" 
          style={{ 
            width: progressWidth,
            transition: 'width 0.3s ease',
          }}
        />
      </div>
      
      {steps.map(({ id, label }) => (
        <div key={id} className="text-center position-relative" style={{ zIndex: 'var(--z-content)' }}>
          <div 
            className={`rounded-circle d-flex align-items-center justify-content-center mx-auto mb-1 fw-semibold ${
              id < currentStep ? 'bg-primary text-white' : 
              id === currentStep ? 'bg-primary text-white' : 
              'bg-white text-muted border border-2'
            }`}
            style={{ width: '28px', height: '28px', fontSize: '0.75rem' }}
          >
            {id < currentStep ? <i className="bi bi-check"></i> : id}
          </div>
          <small className={`fw-medium ${id <= currentStep ? 'text-primary' : 'text-muted'}`} style={{ fontSize: '0.7rem' }}>
            {label}
          </small>
        </div>
      ))}
    </div>
  </div>
))

ProgressSteps.displayName = 'ProgressSteps'

const DeviceIdStep = memo(({ formData, onInputChange }) => (
  <div>
    <div className="text-center mb-3">
      <img 
        src="/images/Altion-final.svg" 
        alt="Revatek Altion Device"
        style={{ width: '60px', height: '60px' }}
        className="mb-2"
      />
      <h6 className="fw-semibold mb-1">Connect Your Revatek Device</h6>
      <p className="text-muted small mb-0">Enter your device&apos;s serial number to get started</p>
    </div>
    
    <div className="card border-0 bg-light mb-3">
      <div className="card-body p-3">
        <div className="mb-0">
          <label htmlFor="serialNumber" className="form-label fw-semibold">
            Device Serial Number <span className="text-danger">*</span>
          </label>
          <input
            type="text"
            className="form-control"
            id="serialNumber"
            placeholder="Enter device serial number"
            value={formData.serialNumber}
            onChange={(e) => onInputChange('serialNumber', e.target.value)}
            style={{ fontFamily: 'monospace', letterSpacing: '1px' }}
          />
          <div className="form-text">
            <i className="bi bi-info-circle me-1"></i>
            You can find this in the Altion Device Info menu
          </div>
        </div>
      </div>
    </div>
  </div>
))

DeviceIdStep.displayName = 'DeviceIdStep'

const DeviceDetailsStep = memo(({ formData, onInputChange }) => (
  <div>
    <div className="text-center mb-3">
      <div className="d-inline-flex align-items-center justify-content-center bg-primary bg-opacity-10 rounded-circle mb-2"
           style={{ width: '48px', height: '48px' }}>
        <i className="bi bi-gear-fill text-primary" style={{ fontSize: '1.2rem' }}></i>
      </div>
      <h6 className="fw-semibold mb-1">Customize Your Device</h6>
      <p className="text-muted small mb-0">Add a name and location to make it easy to identify</p>
    </div>
    
    <div className="row g-3">
      <div className="col-12">
        <div className="card border-0 bg-light">
          <div className="card-body p-3">
            <div className="mb-3">
              <label htmlFor="nickname" className="form-label fw-semibold">
                <i className="bi bi-tag-fill text-primary me-2"></i>
                Device Name
              </label>
              <input
                type="text"
                className="form-control"
                id="nickname"
                placeholder="e.g., Main Battery Bank, Workshop Charger"
                value={formData.nickname}
                onChange={(e) => onInputChange('nickname', e.target.value)}
              />
              <div className="form-text">
                Give your device a memorable name for easy identification
              </div>
            </div>
            
            <div className="mb-0">
              <label htmlFor="location" className="form-label fw-semibold">
                <i className="bi bi-geo-alt-fill text-primary me-2"></i>
                Location
              </label>
              <input
                type="text"
                className="form-control"
                id="location"
                placeholder="e.g., Garage, Boat, RV, Workshop"
                value={formData.location}
                onChange={(e) => onInputChange('location', e.target.value)}
              />
              <div className="form-text">
                Where is this device installed or located?
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <div className="alert alert-info border-0 bg-info bg-opacity-10 mt-3 py-2">
      <i className="bi bi-lightbulb-fill text-info me-2"></i>
      <strong>Tip:</strong> These details help you quickly identify devices when managing multiple units.
    </div>
  </div>
))

DeviceDetailsStep.displayName = 'DeviceDetailsStep'

const ConfirmationStep = memo(({ formData, user }) => (
  <div>
    <div className="text-center mb-3">
      <div className="d-inline-flex align-items-center justify-content-center bg-success bg-opacity-10 rounded-circle mb-2"
           style={{ width: '48px', height: '48px' }}>
        <i className="bi bi-check-circle-fill text-success" style={{ fontSize: '1.2rem' }}></i>
      </div>
      <h6 className="fw-semibold mb-1">Ready to Connect</h6>
      <p className="text-muted small mb-0">Review your device information and confirm to add it to your account</p>
    </div>
    
    <div className="card border-0 shadow-sm mb-3">
      <div className="card-header bg-light border-0 py-2">
        <h6 className="fw-semibold mb-0 small">
          <i className="bi bi-info-circle text-primary me-2"></i>
          Device Summary
        </h6>
      </div>
      <div className="card-body p-3">
        <div className="row g-2">
          <div className="col-md-6">
            <div className="d-flex align-items-start">
              <i className="bi bi-hash text-muted me-2 mt-1"></i>
              <div>
                <small className="text-muted d-block">Serial Number</small>
                <span className="fw-semibold" style={{ fontFamily: 'monospace', fontSize: '0.9rem' }}>
                  {formData.serialNumber}
                </span>
              </div>
            </div>
          </div>
          
          <div className="col-md-6">
            <div className="d-flex align-items-start">
              <i className="bi bi-person text-muted me-2 mt-1"></i>
              <div>
                <small className="text-muted d-block">Added to Account</small>
                <span className="fw-semibold small">{user?.email}</span>
              </div>
            </div>
          </div>
          
          <div className="col-md-6">
            <div className="d-flex align-items-start">
              <i className="bi bi-tag text-muted me-2 mt-1"></i>
              <div>
                <small className="text-muted d-block">Device Name</small>
                <span className="fw-semibold small">
                  {formData.nickname || <span className="text-muted fst-italic">Not specified</span>}
                </span>
              </div>
            </div>
          </div>
          
          <div className="col-md-6">
            <div className="d-flex align-items-start">
              <i className="bi bi-geo-alt text-muted me-2 mt-1"></i>
              <div>
                <small className="text-muted d-block">Location</small>
                <span className="fw-semibold small">
                  {formData.location || <span className="text-muted fst-italic">Not specified</span>}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <div className="alert alert-success border-0 bg-success bg-opacity-10 py-2">
      <div className="d-flex">
        <i className="bi bi-check-circle-fill text-success me-3 mt-1"></i>
        <div>
          <h6 className="alert-heading fw-semibold mb-1 small">Almost Ready!</h6>
          <p className="mb-1 small">
            Once added, your device will appear in your device list and you can begin monitoring immediately.
          </p>
          <small className="text-muted">
            <i className="bi bi-info-circle me-1"></i>
            You may need to configure network settings on the device to enable cloud connectivity.
          </small>
        </div>
      </div>
    </div>
  </div>
))

ConfirmationStep.displayName = 'ConfirmationStep'

// PropTypes for all components
ProgressSteps.propTypes = {
  steps: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number.isRequired,
    label: PropTypes.string.isRequired,
  })).isRequired,
  currentStep: PropTypes.number.isRequired,
  progressWidth: PropTypes.string.isRequired,
}

DeviceIdStep.propTypes = {
  formData: PropTypes.shape({
    serialNumber: PropTypes.string.isRequired,
  }).isRequired,
  onInputChange: PropTypes.func.isRequired,
}

DeviceDetailsStep.propTypes = {
  formData: PropTypes.shape({
    nickname: PropTypes.string.isRequired,
    location: PropTypes.string.isRequired,
  }).isRequired,
  onInputChange: PropTypes.func.isRequired,
}

ConfirmationStep.propTypes = {
  formData: PropTypes.shape({
    serialNumber: PropTypes.string.isRequired,
    nickname: PropTypes.string.isRequired,
    location: PropTypes.string.isRequired,
  }).isRequired,
  user: PropTypes.shape({
    email: PropTypes.string,
  }),
}

DevicePairingWizard.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  initialSerialNumber: PropTypes.string,
}

export default DevicePairingWizard
